import { useAuthStore } from '@/stores/auth.store';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AnimatedCard } from '@/components/ui/animated-card';
import { GlowButton } from '@/components/ui/glow-button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Store, MapPin, CreditCard, BarChart3, AlertCircle, ArrowRight, Sparkles, Rocket, CheckCircle2, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';

export function MerchantDashboardPage() {
  const { user, currentMerchant } = useAuthStore(state => ({
    user: state.user,
    currentMerchant: state.currentMerchant
  }));

  const isPendingSetup = currentMerchant?.applicationStatus === 'pending_setup';
  const isPendingReview = currentMerchant?.needsReview || currentMerchant?.status === 'pending_review';
  const merchantName = currentMerchant?.name || user?.displayName || 'Merchant';

  if (isPendingSetup) {
    return (
      <div className="space-y-6 relative">
        {/* Enhanced Header */}
        <AnimatedCard 
          gradient={true} 
          glow={true} 
          className="p-6 shadow-brand-lg hover:shadow-brand-xl transition-all duration-500 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-primary/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-xl bg-gradient-primary/20 border border-primary/30">
                <Sparkles className="h-6 w-6 text-primary animate-pulse" />
              </div>
              <h1 className="text-3xl font-bold gradient-text-sunrise">Welcome to BevPass!</h1>
            </div>
            <p className="text-muted-foreground text-lg font-medium">
              Complete your merchant setup to start accepting beverage subscriptions
            </p>
          </div>
        </AnimatedCard>

        {/* Enhanced Setup Alert */}
        <AnimatedCard gradient={true} className="p-0 shadow-brand-md border-orange-200/50">
          <Alert className="border-none bg-gradient-to-r from-orange-50/80 to-amber-50/50">
            <div className="p-1 rounded-lg bg-orange-500/20">
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </div>
            <AlertTitle className="text-orange-800 font-semibold">Complete Your Setup</AlertTitle>
            <AlertDescription className="text-orange-700">
              Your merchant account has been created but requires additional setup before you can go live.
            </AlertDescription>
          </Alert>
        </AnimatedCard>

        {/* Enhanced Setup Steps */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatedCard 
            gradient={true} 
            hover3d={true} 
            glow={true}
            className="shadow-brand-sm hover:shadow-brand-lg transition-all duration-500 p-0 group relative"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-3 group-hover:gradient-text-primary transition-all">
                <div className="p-2 rounded-xl bg-green-500/20 border border-green-200/50 group-hover:scale-110 transition-transform">
                  <Store className="h-5 w-5 text-green-600" />
                </div>
                Business Profile
              </CardTitle>
              <CardDescription className="group-hover:text-muted-foreground transition-colors">
                Complete your business information and branding
              </CardDescription>
            </CardHeader>
            <CardContent className="relative z-10">
              <GlowButton asChild variant="default" className="w-full hover:shadow-glow-md group/btn">
                <Link to="/merchants/onboarding/business" className="flex items-center gap-2 justify-center">
                  <Rocket className="w-4 h-4 group-hover/btn:rotate-12 transition-transform flex-shrink-0" />
                  Complete Profile
                  <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform flex-shrink-0" />
                </Link>
              </GlowButton>
            </CardContent>
          </AnimatedCard>

          <AnimatedCard 
            gradient={true} 
                        className="shadow-brand-sm hover:shadow-brand-md transition-all duration-500 p-0 group opacity-60"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-slate-200 border border-slate-300">
                  <MapPin className="h-5 w-5 text-slate-500" />
                </div>
                Locations
              </CardTitle>
              <CardDescription>
                Add your business locations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" disabled>
                Complete business profile first
              </Button>
            </CardContent>
          </AnimatedCard>

          <AnimatedCard 
            gradient={true} 
                        className="shadow-brand-sm hover:shadow-brand-md transition-all duration-500 p-0 group opacity-60"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-slate-200 border border-slate-300">
                  <CreditCard className="h-5 w-5 text-slate-500" />
                </div>
                Subscription Plans
              </CardTitle>
              <CardDescription>
                Create your subscription offerings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" disabled>
                Complete business profile first
              </Button>
            </CardContent>
          </AnimatedCard>
        </div>

        {/* Enhanced Getting Started Guide */}
        <AnimatedCard 
          gradient={true} 
          glow={true}
          className="shadow-brand-lg hover:shadow-brand-xl transition-all duration-500 p-0 group relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <CardHeader className="relative z-10">
            <CardTitle className="flex items-center gap-3 text-xl group-hover:gradient-text-primary transition-all">
              <CheckCircle2 className="h-6 w-6 text-primary group-hover:scale-110 transition-transform" />
              Getting Started Guide
            </CardTitle>
            <CardDescription className="text-base group-hover:text-muted-foreground transition-colors">
              Follow these steps to launch your BevPass subscription service
            </CardDescription>
          </CardHeader>
          
          <CardContent className="relative z-10">
            <ol className="space-y-4">
              <li className="flex gap-4 p-3 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 shadow-brand-sm">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-primary text-white rounded-full flex items-center justify-center text-sm font-bold shadow-glow-sm animate-pulse">
                  1
                </div>
                <div>
                  <p className="font-semibold gradient-text-primary">Complete Business Profile</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add your business details, logo, and branding colors
                  </p>
                </div>
              </li>
              
              {[2, 3, 4, 5].map((step, index) => {
                const titles = ['Add Locations', 'Create Subscription Plans', 'Configure Banking', 'Go Live'];
                const descriptions = [
                  'Configure all your business locations where customers can redeem',
                  'Design your subscription offerings and pricing',
                  'Set up your banking details for payouts',
                  'Request approval to start accepting subscriptions'
                ];
                
                return (
                  <li key={step} className="flex gap-4 p-3 rounded-xl bg-gradient-to-r from-slate-50/50 to-transparent border border-slate-200/50 opacity-50">
                    <div className="flex-shrink-0 w-8 h-8 bg-slate-200 text-slate-500 rounded-full flex items-center justify-center text-sm font-bold">
                      {step}
                    </div>
                    <div>
                      <p className="font-medium text-slate-700">{titles[index]}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {descriptions[index]}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </AnimatedCard>
      </div>
    );
  }

  // Regular merchant dashboard
  return (
    <div className="space-y-6 relative">
      {/* Enhanced Header */}
      <AnimatedCard 
        gradient={true} 
        glow={true} 
        className="p-6 shadow-brand-md hover:shadow-brand-lg transition-all duration-500"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-gradient-primary/20 border border-primary/30">
            <BarChart3 className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold gradient-text-sunrise">{merchantName} Dashboard</h1>
        </div>
        <p className="text-muted-foreground text-lg font-medium">
          Monitor your subscription business performance
        </p>
      </AnimatedCard>

      {/* Enhanced Pending Review Alert */}
      {isPendingReview && (
        <AnimatedCard gradient={true} className="p-0 shadow-brand-md border-amber-200/50">
          <Alert className="border-none bg-gradient-to-r from-amber-50/80 to-yellow-50/50">
            <div className="p-1 rounded-lg bg-amber-500/20">
              <AlertCircle className="h-4 w-4 text-amber-600 animate-pulse" />
            </div>
            <AlertTitle className="text-amber-800 font-semibold">Pending Review</AlertTitle>
            <AlertDescription className="text-amber-700">
              Your merchant account is pending review by our team. You can continue setting up your profile while waiting for approval.
              Once approved, you'll be able to start accepting subscriptions.
            </AlertDescription>
          </Alert>
        </AnimatedCard>
      )}

      {/* Enhanced Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <AnimatedCard 
          gradient={true} 
          hover3d={true} 
          glow={true}
          className="shadow-brand-sm hover:shadow-brand-lg transition-all duration-500 p-0 group relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardHeader className="relative z-10">
            <CardTitle className="flex items-center gap-3 group-hover:gradient-text-primary transition-all">
              <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-200/50 group-hover:scale-110 transition-transform">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              Manage Locations
            </CardTitle>
            <CardDescription className="group-hover:text-muted-foreground transition-colors">
              Add or edit your business locations
            </CardDescription>
          </CardHeader>
          <CardContent className="relative z-10">
            <GlowButton asChild variant="default" className="w-full hover:shadow-glow-md group/btn">
              <Link to="/locations" className="flex items-center gap-2 justify-center">
                <MapPin className="w-4 h-4 group-hover/btn:scale-110 transition-transform flex-shrink-0" />
                Manage Locations
                <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            </GlowButton>
          </CardContent>
        </AnimatedCard>

        <AnimatedCard 
          gradient={true} 
          hover3d={true} 
          glow={true}
          className="shadow-brand-sm hover:shadow-brand-lg transition-all duration-500 p-0 group relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardHeader className="relative z-10">
            <CardTitle className="flex items-center gap-3 group-hover:gradient-text-primary transition-all">
              <div className="p-2 rounded-xl bg-orange-500/20 border border-orange-200/50 group-hover:scale-110 transition-transform">
                <CreditCard className="h-5 w-5 text-orange-600" />
              </div>
              Subscription Plans
            </CardTitle>
            <CardDescription className="group-hover:text-muted-foreground transition-colors">
              Create and manage your subscription offerings
            </CardDescription>
          </CardHeader>
          <CardContent className="relative z-10">
            <GlowButton asChild variant="default" className="w-full hover:shadow-glow-md group/btn">
              <Link to="/plans" className="flex items-center gap-2 justify-center">
                <CreditCard className="w-4 h-4 group-hover/btn:scale-110 transition-transform flex-shrink-0" />
                Manage Plans
                <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            </GlowButton>
          </CardContent>
        </AnimatedCard>

        <AnimatedCard 
          gradient={true} 
          hover3d={true} 
          glow={true}
          className="shadow-brand-sm hover:shadow-brand-lg transition-all duration-500 p-0 group relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardHeader className="relative z-10">
            <CardTitle className="flex items-center gap-3 group-hover:gradient-text-primary transition-all">
              <div className="p-2 rounded-xl bg-blue-500/20 border border-blue-200/50 group-hover:scale-110 transition-transform">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              Usage & Redemptions
            </CardTitle>
            <CardDescription className="group-hover:text-muted-foreground transition-colors">
              Monitor usage and redemption activity
            </CardDescription>
          </CardHeader>
          <CardContent className="relative z-10">
            <GlowButton asChild variant="default" className="w-full hover:shadow-glow-md group/btn">
              <Link to="/redemptions" className="flex items-center gap-2 justify-center">
                <Activity className="w-4 h-4 group-hover/btn:scale-110 transition-transform flex-shrink-0" />
                View Usage & Redemptions
                <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            </GlowButton>
          </CardContent>
        </AnimatedCard>
      </div>
    </div>
  );
}
